<!--
  LIST перечень: inner 12-col grid — Наименование + dynamic columns + Действия (fixed 1).
  Matching list palette drops are handled on the grid row (PassportBuilderPage). Reorder/resize/delete dynamic columns.
-->
<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref } from 'vue'
import { VIcons } from '@/shared/ui'
import type {
  GridFieldType,
  SummaryLayoutItem,
  TableDynamicColumn,
} from '@/features/Inventory/ObjectTypePassportGrid/model/types'
import type { ObjectInventoryField } from '@/entities/Inventory/ObjectTypes'
import type { DragPayload } from '../model/types'
import { createDragPayload, parseDragTransfer, PB_DND_MIME } from '../model/useDragDrop'
import {
  addSummaryFromTableColumnSegment,
  buildSegmentOrder,
  insertIndexSummaryFromPointer,
  PB_TABLE_NAME_SEGMENT_ID,
  removeSummaryLayoutItem,
  removeTableColumn,
  renameTableColumn,
  reorderSummaryLayoutItems,
  reorderTableSegments,
  resizeAdjacentPair,
  updateSummaryLayoutItemLabel,
  updateSummaryLayoutItemLength,
} from '../model/tableLayout'
import PassportBuilderSummaryFieldCard from './PassportBuilderSummaryFieldCard.vue'

const INNER_COLS = 12
const ACTIONS_SPAN = 1

const props = defineProps<{
  field: GridFieldType
  groupName?: string
  /** Inventory fields (for preview row defaults). */
  allFields?: ObjectInventoryField[]
  /** Current palette/grid drag (for drop highlight; dragover cannot read getData). */
  activeDrag?: DragPayload | null
}>()

const emit = defineEmits<{
  dragstart: [payload: DragPayload, event: DragEvent]
  patch: [updater: (f: GridFieldType) => GridFieldType]
  'builder-drag-end': []
  'summary-field-settings': [payload: { tableFieldId: string; layoutItemId: string }]
}>()

function listGroupMatchesField(dragGroupId: string | number | undefined, tableGroupId: number | undefined): boolean {
  if (dragGroupId == null || tableGroupId == null) return false
  return String(dragGroupId).trim() === String(tableGroupId).trim()
}

/** LIST palette row may omit `list_group_id` — recover from inventory `group` (same as PassportBuilderPage). */
function resolvePaletteDragListGroupId(data: DragPayload): number | null {
  const raw = data.field.list_group_id
  if (raw != null && Number.isFinite(Number(raw))) return Number(raw)
  const inv = (props.allFields ?? []).find((x) => String(x.id) === String(data.field.id))
  if (inv?.group != null && Number.isFinite(Number(inv.group))) return Number(inv.group)
  return null
}

function isPaletteFieldForThisTable(data: DragPayload | null): boolean {
  if (!data) return false
  if (data.source !== 'palette' || data.itemType !== 'field') return false
  const dragGid = resolvePaletteDragListGroupId(data)
  if (dragGid == null || props.field.list_group_id == null) return false
  return listGroupMatchesField(dragGid, props.field.list_group_id)
}

const headerDragDepth = ref(0)

const headerDragActive = computed(() => headerDragDepth.value > 0)

const headerTitle = computed(() => {
  return props.groupName?.trim() || props.field.name?.trim() || '—'
})

const headerDropClass = computed(() => ({
  'pb-table-block-header--drag-over': headerDragActive.value,
}))

const gridColTemplate = computed(() => `repeat(${INNER_COLS}, minmax(0, 1fr))`)

const nameSpan = computed(() => props.field.table_name_span ?? 11)
/** Visual order: name segment + dynamic column ids. */
const segmentOrder = computed(() => buildSegmentOrder(props.field))
const colById = computed(() => {
  const m = new Map<string, TableDynamicColumn>()
  for (const c of props.field.table_columns ?? []) {
    m.set(String(c.id), c)
  }
  return m
})

function fieldById(id: string | number): ObjectInventoryField | undefined {
  const list = props.allFields ?? []
  return list.find((f) => String(f.id) === String(id))
}

/** Preview cell: field default when set, otherwise «-». */
function previewFromField(inv: ObjectInventoryField | undefined): string {
  if (!inv) return '-'
  const dv = inv.default_value
  if (Array.isArray(dv)) {
    const parts = dv.map((x) => String(x).trim()).filter(Boolean)
    return parts.length ? parts.join(', ') : '-'
  }
  if (dv == null) return '-'
  const s = String(dv).trim()
  return s || '-'
}

/** «Наименование» preview: prefer a field in the list group named like title, else first field in group. */
const nameColumnPreview = computed(() => {
  const gid = props.field.list_group_id
  const list = props.allFields ?? []
  if (gid == null) return '-'
  const inGroup = list.filter((f) => Number(f.group) === Number(gid))
  if (inGroup.length === 0) return '-'
  const titled = inGroup.find((f) => /наимен/i.test(f.name))
  return previewFromField(titled ?? inGroup[0])
})

function previewForDynColumn(col: TableDynamicColumn | undefined): string {
  if (!col) return '-'
  return previewFromField(fieldById(col.field_id))
}

/** Pairs (leftIdx) for resize: all flexible segments except no pair ending at actions. */
function apply(updater: (f: GridFieldType) => GridFieldType) {
  emit('patch', updater)
}

function onSummaryModeChange(v: boolean | null) {
  apply((f) => ({ ...f, summary_mode: !!v }))
}

function nameColumnRefId(): string | number | null {
  const gid = props.field.list_group_id
  const list = props.allFields ?? []
  if (gid == null) return null
  const inGroup = list.filter((x) => Number(x.group) === Number(gid))
  if (inGroup.length === 0) return null
  const titled = inGroup.find((x) => /наимен/i.test(x.name))
  const id = (titled ?? inGroup[0]).id
  if (id == null || String(id) === '') return null
  return id as string | number
}

function summaryInvField(item: SummaryLayoutItem) {
  const list = props.allFields ?? []
  return list.find((f) => String(f.id) === String(item.ref_id))
}

function summaryDisplayName(item: SummaryLayoutItem): string {
  const custom = item.label?.trim()
  if (custom) return custom
  const inv = summaryInvField(item)
  const n = inv?.name?.trim()
  if (n) return n
  const rid = item.ref_id
  if (rid == null || (typeof rid === 'number' && !Number.isFinite(rid))) return 'Поле'
  return `Поле ${String(rid)}`
}

function summaryFieldTypeBadge(item: SummaryLayoutItem): string {
  const t = summaryInvField(item)?.type
  if (!t) return 'Текст'
  const key = t.toLowerCase()
  const map: Record<string, string> = {
    string: 'Текст',
    text: 'Текст',
    number: 'Число',
    integer: 'Число',
    float: 'Число',
    date: 'Дата',
    datetime: 'Дата',
    bool: 'Флаг',
    boolean: 'Флаг',
  }
  return map[key] ?? t
}

function summaryIsParent(item: SummaryLayoutItem): boolean {
  const inv = summaryInvField(item)?.inventory_type
  return inv != null && Number(inv) > 0
}

function onSummaryRemove(itemId: string) {
  apply((f) => removeSummaryLayoutItem(f, itemId))
}

function onSummaryResize(itemId: string, newSpan: number) {
  apply((f) => updateSummaryLayoutItemLength(f, itemId, newSpan))
}

function onSummarySettings(itemId: string) {
  emit('summary-field-settings', { tableFieldId: String(props.field.id), layoutItemId: itemId })
}

function onSummaryUpdateLabel(itemId: string, label: string | undefined) {
  apply((f) => updateSummaryLayoutItemLabel(f, itemId, label))
}

function onSummarySubgridDragOver(event: DragEvent) {
  event.preventDefault()
  if (!event.dataTransfer) return
  const data = props.activeDrag ?? parseDragTransfer(event)
  if (data?.itemType === 'summary-item') {
    event.dataTransfer.dropEffect = 'move'
  } else if (data?.itemType === 'table-column') {
    event.dataTransfer.dropEffect = 'copy'
  } else if (isPaletteFieldForThisTable(data)) {
    event.dataTransfer.dropEffect = 'copy'
  } else {
    event.dataTransfer.dropEffect = 'none'
  }
}

function onSummarySubgridDrop(event: DragEvent) {
  event.preventDefault()
  const data = props.activeDrag ?? parseDragTransfer(event)
  if (data?.source === 'palette' && data.itemType === 'field') {
    headerDragDepth.value = 0
    return
  }
  event.stopPropagation()
  headerDragDepth.value = 0
  if (!data) return
  const el = (event.currentTarget as HTMLElement).closest('.pb-summary-subgrid') as HTMLElement | null
  const rect = el?.getBoundingClientRect()
  const n = props.field.summary_layout?.length ?? 0
  const insertIdx =
    rect && rect.width > 0
      ? insertIndexSummaryFromPointer(event.clientX - rect.left, rect.width, n)
      : n

  if (data.itemType === 'table-column' && data.field.table_field_id === props.field.id) {
    const segId = String(data.field.id)
    const nameRef = nameColumnRefId()
    const nameLabel =
      nameRef != null ? fieldById(nameRef)?.name?.trim() || undefined : undefined
    apply((f) => addSummaryFromTableColumnSegment(f, segId, insertIdx, nameRef, nameLabel))
  } else if (
    data.itemType === 'summary-item' &&
    data.field.table_field_id === props.field.id &&
    data.field.summary_item_id
  ) {
    const fromIdx =
      props.field.summary_layout?.findIndex((x) => x.id === data.field.summary_item_id) ?? -1
    if (fromIdx === -1) return
    apply((f) => reorderSummaryLayoutItems(f, fromIdx, insertIdx))
  }
  emit('builder-drag-end')
}

function onHeaderDragEnter(event: DragEvent) {
  event.preventDefault()
  headerDragDepth.value += 1
}

function onHeaderDragLeave(event: DragEvent) {
  event.preventDefault()
  headerDragDepth.value = Math.max(0, headerDragDepth.value - 1)
}

/** Amber title bar: allow column reorder chrome (move) or palette add (copy). */
function onHeaderDragOver(event: DragEvent) {
  event.preventDefault()
  if (props.field.summary_mode) {
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'none'
    return
  }
  const data = props.activeDrag ?? parseDragTransfer(event)
  const okPalette = isPaletteFieldForThisTable(data)
  if (event.dataTransfer) {
    event.dataTransfer.dropEffect = okPalette ? 'copy' : 'move'
  }
}

function onHeaderDrop(event: DragEvent) {
  event.preventDefault()
  headerDragDepth.value = 0
}

/** «Действия» header: allow drop + bubble to `.pb-grid-row` for list palette adds. */
function onActionsHeaderDragOver(event: DragEvent) {
  event.preventDefault()
  if (props.field.summary_mode) {
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'none'
    return
  }
  const data = props.activeDrag ?? parseDragTransfer(event)
  const okPalette = isPaletteFieldForThisTable(data)
  if (event.dataTransfer) {
    event.dataTransfer.dropEffect = okPalette ? 'copy' : 'none'
  }
}

function onActionsHeaderDrop(event: DragEvent) {
  event.preventDefault()
}

function onTableBlockDragStart(event: DragEvent) {
  const f = props.field
  const payload = createDragPayload('grid', 'table', {
    id: f.id,
    type: 'table',
    name: f.name,
    list_group_id: f.list_group_id,
  })
  emit('dragstart', payload, event)
}

function onNameHeaderDragStart(event: DragEvent) {
  event.stopPropagation()
  const payload = createDragPayload('grid', 'table-column', {
    id: PB_TABLE_NAME_SEGMENT_ID,
    name: '',
    type: 'field',
    table_field_id: props.field.id,
  })
  emit('dragstart', payload, event)
  if (event.dataTransfer) {
    event.dataTransfer.setData(PB_DND_MIME, payload.payloadJson)
    event.dataTransfer.setData('application/json', payload.payloadJson)
  }
}

function onDynSegmentDragStart(segId: string, event: DragEvent) {
  const col = colById.value.get(segId)
  if (!col) return
  onDynColDragStart(col, event)
}

function onDynColDragStart(col: TableDynamicColumn, event: DragEvent) {
  event.stopPropagation()
  const payload = createDragPayload('grid', 'table-column', {
    id: col.id,
    name: '',
    type: 'field',
    table_field_id: props.field.id,
  })
  emit('dragstart', payload, event)
  if (event.dataTransfer) {
    event.dataTransfer.setData(PB_DND_MIME, payload.payloadJson)
    event.dataTransfer.setData('application/json', payload.payloadJson)
  }
}

/** Inner header cells: reorder own table columns only. Palette «Перечень» drops must bubble to the grid row. */
function onSegmentHeaderDrop(event: DragEvent, targetSegIndex: number) {
  event.preventDefault()
  const payload = parseDragTransfer(event)
  if (!payload || payload.itemType !== 'table-column') return
  event.stopPropagation()
  if (payload.field.table_field_id !== props.field.id) return
  const order = buildSegmentOrder(props.field)
  const fromIdx = order.findIndex((id) => id === payload.field.id)
  if (fromIdx === -1) return
  if (fromIdx === targetSegIndex) return
  apply((f) => reorderTableSegments(f, fromIdx, targetSegIndex))
  emit('builder-drag-end')
}

function removeDynColumn(colId: string) {
  apply((f) => removeTableColumn(f, colId))
}

const editingSegId = ref<string | null>(null)
const editingNameDraft = ref('')
const renameNameBaseline = ref('')

/** Segment ids from order can be string or number (API); editingSegId is always string. */
function isEditingSeg(segId: string | number): boolean {
  return editingSegId.value !== null && String(editingSegId.value) === String(segId)
}

async function startRenameColumn(segId: string) {
  const col = props.field.table_columns?.find((c) => String(c.id) === String(segId))
  if (!col) return
  editingSegId.value = String(segId)
  editingNameDraft.value = col.name
  renameNameBaseline.value = col.name
  await nextTick()
  const safe = typeof CSS !== 'undefined' && 'escape' in CSS ? CSS.escape(String(segId)) : String(segId).replace(/"/g, '')
  const el = document.querySelector(`[data-pb-rename-col="${safe}"]`) as HTMLInputElement | null
  el?.focus()
  el?.select()
}

function commitRenameColumn() {
  const id = editingSegId.value
  if (!id) return
  const fallback = renameNameBaseline.value
  /** Defer so v-model has the last keystroke; skip if Escape cleared edit in the same tick. */
  void nextTick(() => {
    if (editingSegId.value !== id) return
    const draft = editingNameDraft.value
    editingSegId.value = null
    const next = draft.trim() || fallback
    apply((f) => renameTableColumn(f, id, next))
  })
}

function cancelRenameColumn() {
  editingSegId.value = null
}

let resizeState: {
  pairIndex: number
  startX: number
  elW: number
} | null = null

function onResizePointerDown(pairIndex: number, event: PointerEvent) {
  event.preventDefault()
  event.stopPropagation()
  const row = (event.target as HTMLElement).closest('.pb-table-block-grid-row') as HTMLElement | null
  const w = row?.getBoundingClientRect().width ?? 1
  resizeState = { pairIndex, startX: event.clientX, elW: w }
  document.addEventListener('pointermove', onResizePointerMove)
  document.addEventListener('pointerup', onResizePointerUp, { once: true })
}

function onResizePointerMove(event: PointerEvent) {
  if (!resizeState) return
  const dx = event.clientX - resizeState.startX
  const deltaCols = Math.round((dx / resizeState.elW) * INNER_COLS)
  if (deltaCols === 0) return
  apply((f) => resizeAdjacentPair(f, resizeState!.pairIndex, deltaCols))
  resizeState.startX = event.clientX
}

function onResizePointerUp() {
  resizeState = null
  document.removeEventListener('pointermove', onResizePointerMove)
}

onBeforeUnmount(() => {
  document.removeEventListener('pointermove', onResizePointerMove)
})
</script>

<template>
  <div
    class="pb-table-block"
    :data-pb-table-id="String(field.id)"
    :style="{ gridColumn: `span ${field.length || 12}` }"
    draggable="true"
    @dragstart="onTableBlockDragStart"
  >
    <div
      class="pb-table-block-header pb-table-block-header--amber"
      :class="headerDropClass"
      @dragenter="onHeaderDragEnter"
      @dragleave="onHeaderDragLeave"
      @dragover="onHeaderDragOver"
      @drop="onHeaderDrop"
    >
      <VIcons type="table_chart" class="pb-tbh-icon" aria-hidden="true" />
      <span class="pb-table-block-header__title">{{ headerTitle }}</span>
      <div class="pb-table-block-header__tools">
        <span class="pb-table-block-header__switch-label">Сводная информация</span>
        <div class="pb-table-block-header__switch-wrap">
          <VCheckbox
            class="pb-table-block-header__switch"
            :model-value="!!field.summary_mode"
            hide-details
            density="compact"
            color="primary"
            aria-label="Сводная информация"
            @update:model-value="onSummaryModeChange"
          />
        </div>
      </div>
    </div>

    <div
      v-if="field.summary_mode"
      class="pb-table-block-body pb-table-block-body--summary"
      role="region"
      :aria-label="`Сводная информация: ${headerTitle}`"
    >
      <div
        class="pb-summary-subgrid"
        role="group"
        :aria-label="`Поля сводной информации для ${headerTitle}`"
        :style="{ gridTemplateColumns: gridColTemplate }"
        @dragover="onSummarySubgridDragOver"
        @drop="onSummarySubgridDrop"
      >
        <PassportBuilderSummaryFieldCard
          v-for="item in field.summary_layout ?? []"
          :key="item.id"
          :item="item"
          :table-field-id="String(field.id)"
          :display-name="summaryDisplayName(item)"
          :field-type-badge="summaryFieldTypeBadge(item)"
          :is-parent="summaryIsParent(item)"
          :parent-badge="''"
          @remove="onSummaryRemove"
          @resize="onSummaryResize"
          @dragstart="(p, e) => emit('dragstart', p, e)"
          @settings="onSummarySettings"
          @update-label="onSummaryUpdateLabel"
        />
        <div
          v-if="(field.summary_layout?.length ?? 0) === 0"
          class="pb-table-block-summary-placeholder"
          role="status"
        >
          Перетащите поля из соответствующего перечня (режим сводной информации).
        </div>
      </div>
    </div>

    <div v-else class="pb-table-block-body">
      <!-- Header row -->
      <div
        class="pb-table-block-grid-row pb-table-block-grid-row--head"
        :style="{ gridTemplateColumns: gridColTemplate }"
      >
        <template v-for="(segId, segIdx) in segmentOrder" :key="'h-' + segId">
          <div
            v-if="segId === PB_TABLE_NAME_SEGMENT_ID"
            class="pb-table-block-cell pb-table-block-cell--head pb-table-block-cell--name"
            :style="{ gridColumn: `span ${nameSpan}` }"
            draggable="true"
            @dragstart="onNameHeaderDragStart($event)"
            @dragover.prevent
            @drop="onSegmentHeaderDrop($event, segIdx)"
          >
            <span class="pb-table-block-cell__label">Наименование</span>
            <span
              v-if="segIdx < segmentOrder.length - 1"
              class="pb-table-resize"
              @pointerdown.stop="onResizePointerDown(segIdx, $event)"
            />
          </div>
          <div
            v-else
            class="pb-table-block-cell pb-table-block-cell--head pb-table-block-cell--dyn"
            :style="{ gridColumn: `span ${colById.get(String(segId))?.span ?? 1}` }"
            :draggable="!isEditingSeg(segId)"
            @dragstart="onDynSegmentDragStart(segId, $event)"
            @dragover.prevent
            @drop="onSegmentHeaderDrop($event, segIdx)"
          >
            <input
              v-if="isEditingSeg(segId)"
              :data-pb-rename-col="String(segId)"
              v-model="editingNameDraft"
              class="pb-table-col-rename-input"
              type="text"
              @keydown.enter.prevent="commitRenameColumn"
              @keydown.escape.prevent="cancelRenameColumn"
              @blur="commitRenameColumn"
              @click.stop
              @mousedown.stop
            />
            <span
              v-else
              class="pb-table-block-cell__label pb-table-block-cell__label--renameable"
              title="Двойной щелчок — переименовать"
              @dblclick.stop.prevent="startRenameColumn(String(segId))"
            >{{ colById.get(String(segId))?.name ?? '' }}</span>
            <VBtn
              icon
              size="x-small"
              variant="text"
              density="compact"
              class="pb-table-col-delete"
              draggable="false"
              aria-label="Удалить колонку"
              @mousedown.stop
              @click.stop="removeDynColumn(String(segId))"
            >
              <VIcons type="close" />
            </VBtn>
            <span
              v-if="segIdx < segmentOrder.length - 1"
              class="pb-table-resize"
              @pointerdown.stop="onResizePointerDown(segIdx, $event)"
            />
          </div>
        </template>
        <div
          class="pb-table-block-cell pb-table-block-cell--head pb-table-block-cell--actions"
          :style="{ gridColumn: `span ${ACTIONS_SPAN}` }"
          draggable="false"
          @dragover="onActionsHeaderDragOver"
          @drop="onActionsHeaderDrop"
        >
          Действия
        </div>
      </div>

      <!-- Body row -->
      <div
        class="pb-table-block-grid-row pb-table-block-grid-row--body"
        :style="{ gridTemplateColumns: gridColTemplate }"
      >
        <template v-for="segId in segmentOrder" :key="'b-' + segId">
          <div
            v-if="segId === PB_TABLE_NAME_SEGMENT_ID"
            class="pb-table-block-cell pb-table-block-cell--body pb-table-block-cell--name"
            :style="{ gridColumn: `span ${nameSpan}` }"
            draggable="false"
          >
            {{ nameColumnPreview }}
          </div>
          <div
            v-else
            class="pb-table-block-cell pb-table-block-cell--body pb-table-block-cell--dyn"
            :style="{ gridColumn: `span ${colById.get(String(segId))?.span ?? 1}` }"
            draggable="false"
          >
            {{ previewForDynColumn(colById.get(String(segId)) ?? undefined) }}
          </div>
        </template>
        <div
          class="pb-table-block-cell pb-table-block-cell--body pb-table-block-cell--actions"
          :style="{ gridColumn: `span ${ACTIONS_SPAN}` }"
          draggable="false"
        >
          <div class="pb-table-block-actions">
            <VBtn
              icon
              size="x-small"
              density="compact"
              color="primary"
              variant="flat"
              class="text-none pb-table-block-actions__btn"
              tabindex="-1"
              aria-hidden="true"
              @click.prevent.stop
            >
              <VIcons type="visibility" class="pb-table-block-actions__icon" />
            </VBtn>
            <VBtn
              icon
              size="x-small"
              density="compact"
              color="error"
              variant="flat"
              class="text-none pb-table-block-actions__btn"
              tabindex="-1"
              aria-hidden="true"
              @click.prevent.stop
            >
              <VIcons type="delete" class="pb-table-block-actions__icon" />
            </VBtn>
            <VBtn
              icon
              size="x-small"
              density="compact"
              color="default"
              variant="tonal"
              class="text-none pb-table-block-actions__btn"
              tabindex="-1"
              aria-hidden="true"
              @click.prevent.stop
            >
              <VIcons type="link" class="pb-table-block-actions__icon" />
            </VBtn>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.pb-table-block {
  position: relative;
  border-radius: 4px;
  transition:
    box-shadow 0.12s ease,
    background 0.12s ease;
}

.pb-table-block-header__title {
  flex: 1;
  min-width: 0;
}

.pb-table-block-header__tools {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.pb-table-block-header__switch-label {
  font-size: 10px;
  font-weight: 500;
  opacity: 0.95;
  white-space: nowrap;
}

.pb-table-block-header__switch-wrap {
  display: flex;
  align-items: center;
  line-height: 0;
}

.pb-table-block-header__switch {
  transform: scale(0.72);
  transform-origin: center right;
}

.pb-table-block-body--summary {
  min-height: 48px;
}

.pb-summary-subgrid {
  display: grid;
  width: 100%;
  column-gap: 8px;
  row-gap: 8px;
  align-items: stretch;
  min-height: 48px;
  padding: 10px 10px 12px;
  box-sizing: border-box;
}

.pb-table-block-summary-placeholder {
  grid-column: 1 / -1;
  padding: 12px 8px;
  font-size: 12px;
  color: var(--pb-text-secondary, rgba(0, 0, 0, 0.6));
  text-align: center;
  min-height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.pb-table-block-body {
  font-size: 12px;
  background: var(--pb-bg-canvas, #fff);
  position: relative;
  min-height: 48px;
}

.pb-table-block-grid-row {
  display: grid;
  width: 100%;
  column-gap: 0;
  min-height: 38px;
  align-items: stretch;
}

.pb-table-block-grid-row--head {
  border-bottom: 1px solid var(--pb-border, #e0e0e0);
}

.pb-table-block-grid-row--body {
  border-bottom: 1px solid var(--pb-border-light, #eeeeee);

  &:last-child {
    border-bottom: none;
  }
}

.pb-table-block-cell {
  padding: 8px 10px;
  color: var(--pb-text-secondary, rgba(0, 0, 0, 0.6));
  min-width: 0;
  position: relative;
}

.pb-table-block-cell--head {
  background: var(--pb-bg-page, #fafafa);
  font-weight: 500;
  text-align: left;
}

.pb-table-block-cell--head.pb-table-block-cell--name {
  display: flex;
  align-items: center;
  min-width: 0;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
}

.pb-table-block-cell--head.pb-table-block-cell--name .pb-table-block-cell__label {
  flex: 1;
  min-width: 0;
}

.pb-table-block-cell--name:not(.pb-table-block-cell--head) {
  cursor: default;
}

.pb-table-block-cell--dyn {
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
}

.pb-table-block-cell--actions {
  cursor: default;
}

.pb-table-block-cell--body.pb-table-block-cell--actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.pb-table-block-cell--body.pb-table-block-cell--name,
.pb-table-block-cell--body.pb-table-block-cell--dyn {
  color: var(--pb-text-primary, rgba(0, 0, 0, 0.87));
  display: flex;
  align-items: center;
  min-height: 36px;
}

.pb-table-block-cell__label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pb-table-block-cell__label--renameable {
  cursor: text;
  user-select: none;
}

.pb-table-col-rename-input {
  flex: 1;
  min-width: 0;
  box-sizing: border-box;
  font: inherit;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.2;
  padding: 2px 4px;
  border: 1px solid var(--pb-primary, #1976d2);
  border-radius: 4px;
  background: var(--pb-bg-canvas, #fff);
  color: var(--pb-text-primary, rgba(0, 0, 0, 0.87));
  text-transform: uppercase;
  letter-spacing: 0.25px;
}

.pb-table-col-rename-input:focus {
  outline: none;
  box-shadow: 0 0 0 1px rgba(25, 118, 210, 0.35);
}

.pb-table-block-cell--head.pb-table-block-cell--dyn {
  display: flex;
  align-items: center;
  gap: 4px;
}

.pb-table-col-delete {
  flex-shrink: 0;
  opacity: 0.45;

  &:hover {
    opacity: 1;
    color: #e53935 !important;
  }
}

.pb-table-resize {
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 5px;
  margin-right: -2px;
  cursor: col-resize;
  z-index: 2;
  background: transparent;

  &:hover {
    background: rgba(25, 118, 210, 0.15);
  }
}

.pb-table-block-cell--actions {
  padding-left: 6px;
  padding-right: 6px;
  overflow: hidden;
}

.pb-table-block-cell--head.pb-table-block-cell--actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  text-align: right;
}

.pb-table-block-actions {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: flex-end;
  gap: 2px;
  min-width: 0;
  width: 100%;
}

.pb-table-block-actions__btn {
  flex-shrink: 0;
  min-width: 20px !important;
  width: 20px !important;
  height: 20px !important;
  padding: 0 !important;
}

.pb-table-block-actions__btn :deep(.v-btn__overlay) {
  border-radius: 50%;
}

.pb-table-block-actions__icon {
  font-size: 14px !important;
  line-height: 1 !important;
  width: 14px !important;
  height: 14px !important;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
}
</style>
